import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as S from "./IASistemasStyle";
import { iaService } from "../../Services/iaService";

/**
 * Aba de conversas (Leads Instagram / Leads Website / Clientes Plataforma).
 * Estado persistido na URL: ?tab=...&chat=<id>&tema=...&rec=1&q=busca
 */

const TEMA_LABELS = {
  pagamento_saque: "Pagamento/Saque",
  contrato_minerios: "Contratos",
  golden_box: "Golden Box",
  cadastro_dados: "Cadastro",
  auditoria: "Auditoria",
  interesse_comercial: "Interesse Comercial",
  suporte_tecnico: "Suporte Técnico",
  atendimento_humano: "Atendimento Humano",
  outro: "Outro",
};

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

/**
 * Converte as marcações usadas pela IA (<negrito>, <italico>, **md**) em
 * elementos React de verdade. Seguro contra XSS: nada vira HTML cru.
 */
function renderFormatted(text) {
  const source = String(text || "");
  const regex =
    /<negrito>([\s\S]*?)<\/negrito>|<italico>([\s\S]*?)<\/italico>|\*\*([\s\S]*?)\*\*/g;
  const parts = [];
  let last = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(source))) {
    if (match.index > last) parts.push(source.slice(last, match.index));
    if (match[1] !== undefined) parts.push(<strong key={key++}>{match[1]}</strong>);
    else if (match[2] !== undefined) parts.push(<i key={key++}>{match[2]}</i>);
    else parts.push(<strong key={key++}>{match[3]}</strong>);
    last = regex.lastIndex;
  }
  if (last < source.length) parts.push(source.slice(last));
  return parts;
}

/** Remove marcações para exibição em previews de uma linha. */
const stripFormatting = (text) =>
  String(text || "")
    .replace(/<\/?negrito>|<\/?italico>|\*\*/g, "")
    .trim();

const hora = (d) =>
  d
    ? new Date(d).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

export default function ChatsTab({ platform, titulo }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  // Controle de auto-scroll: só desce sozinho se o usuário já estava no fim
  const atBottomRef = useRef(true);
  const lastMsgKeyRef = useRef("");

  // Filtros e busca inicializados da URL
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [filtroTema, setFiltroTema] = useState(searchParams.get("tema") || "");
  const [soReclamacoes, setSoReclamacoes] = useState(searchParams.get("rec") === "1");
  const urlChatId = searchParams.get("chat");

  /** Atualiza parâmetros da URL preservando os demais (replace: não polui histórico). */
  const syncUrl = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === undefined || v === null || v === "" || v === false) p.delete(k);
            else p.set(k, String(v));
          });
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const loadChats = useCallback(async () => {
    try {
      const res = await iaService.getChats(platform, {
        search,
        limit: 60,
        tema: filtroTema || undefined,
        reclamacao: soReclamacoes || undefined,
      });
      setChats(res.data || []);
    } catch (e) {
      console.error("Erro ao listar chats:", e);
    } finally {
      setLoading(false);
    }
  }, [platform, search, filtroTema, soReclamacoes]);

  const loadMessages = useCallback(async (chat) => {
    try {
      const res = await iaService.getChatMessages(chat.id, { limit: 300 });
      const data = res.data || [];
      // Se nada mudou, mantém o array anterior (não re-renderiza nem mexe no scroll)
      setMessages((prev) => {
        const same =
          prev.length === data.length &&
          prev[prev.length - 1]?.id === data[data.length - 1]?.id;
        return same ? prev : data;
      });
    } catch (e) {
      console.error("Erro ao carregar mensagens:", e);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadChats();
  }, [loadChats]);

  // Restaura o chat ativo da URL quando a lista chega (sobrevive ao refresh)
  useEffect(() => {
    if (!urlChatId || chats.length === 0) return;
    if (selected && String(selected.id) === urlChatId) return;
    const found = chats.find((c) => String(c.id) === urlChatId);
    if (found) {
      setSelected(found);
      loadMessages(found);
    }
  }, [chats, urlChatId, selected, loadMessages]);

  // Atualiza a conversa aberta a cada 10s
  useEffect(() => {
    if (!selected) return;
    const interval = setInterval(() => loadMessages(selected), 10000);
    return () => clearInterval(interval);
  }, [selected, loadMessages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !selected) return;

    const key = `${selected.id}:${messages.length}:${messages[messages.length - 1]?.id || 0}`;
    if (key === lastMsgKeyRef.current) return; // nada novo — não mexe no scroll

    const chatChanged = !lastMsgKeyRef.current.startsWith(`${selected.id}:`);
    lastMsgKeyRef.current = key;

    // Desce pro fim só ao abrir a conversa ou se o usuário já estava no fim
    if (chatChanged || atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, selected]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const handleSelect = (chat) => {
    setSelected(chat);
    loadMessages(chat);
    syncUrl({ chat: chat.id });
  };

  const handleSearch = (value) => {
    setSearch(value);
    syncUrl({ q: value });
  };

  const handleTema = (value) => {
    setFiltroTema(value);
    syncUrl({ tema: value });
  };

  const handleReclamacoes = (checked) => {
    setSoReclamacoes(checked);
    syncUrl({ rec: checked ? "1" : "" });
  };

  const handleStatus = async (status) => {
    if (!selected) return;
    const labels = {
      active: "desbloquear esta conversa (a IA volta a responder)",
      blocked: "BLOQUEAR este contato (a IA deixa de responder)",
    };
    if (!window.confirm(`Deseja ${labels[status]}?`)) return;
    try {
      await iaService.setChatStatus(selected.id, status);
      setSelected({ ...selected, status });
      await loadChats();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao alterar status.");
    }
  };

  const statusBadge = (status) => {
    if (status === "paused") return <S.Badge $tipo="paused">IA Pausada</S.Badge>;
    if (status === "blocked") return <S.Badge $tipo="blocked">Bloqueado</S.Badge>;
    return null;
  };

  return (
    <S.ChatLayout>
      <S.ChatListPanel>
        <S.SearchInput
          placeholder={`Buscar em ${titulo}...`}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <S.FilterRow>
          <select value={filtroTema} onChange={(e) => handleTema(e.target.value)}>
            <option value="">Todos os temas</option>
            {Object.entries(TEMA_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <label>
            <input
              type="checkbox"
              checked={soReclamacoes}
              onChange={(e) => handleReclamacoes(e.target.checked)}
            />
            Só reclamações
          </label>
        </S.FilterRow>
        <S.ChatListScroll>
          {loading ? (
            <S.LoadingText>Carregando...</S.LoadingText>
          ) : chats.length === 0 ? (
            <S.LoadingText>Nenhuma conversa encontrada.</S.LoadingText>
          ) : (
            chats.map((chat) => (
              <S.ChatListItem
                key={chat.id}
                $selected={selected?.id === chat.id}
                onClick={() => handleSelect(chat)}
              >
                <div className="nome">
                  {chat.name || chat.external_id}
                  <time>
                    {chat.last_message_at
                      ? new Date(chat.last_message_at).toLocaleDateString("pt-BR")
                      : ""}
                  </time>
                </div>
                <div className="preview">
                  {chat.metadata?.ia_tags?.resumo ||
                    stripFormatting(chat.last_message_content) ||
                    "—"}
                </div>
                <div className="meta">
                  {chat.metadata?.ia_tags?.reclamacao && (
                    <S.Badge $tipo="urgente">Reclamação</S.Badge>
                  )}
                  {chat.metadata?.ia_tags?.sentimento === "negativo" && (
                    <S.Badge $tipo="negativo">Negativo</S.Badge>
                  )}
                  {(chat.metadata?.ia_tags?.temas || []).slice(0, 2).map((t) => (
                    <S.Badge key={t} $tipo="tema">
                      {TEMA_LABELS[t] || t}
                    </S.Badge>
                  ))}
                  {chat.budget && <S.Badge $tipo="budget">{chat.budget}</S.Badge>}
                  {statusBadge(chat.status)}
                </div>
              </S.ChatListItem>
            ))
          )}
        </S.ChatListScroll>
      </S.ChatListPanel>

      <S.ChatWindow>
        {!selected ? (
          <S.EmptyState>
            <strong>Nenhuma conversa selecionada</strong>
            Escolha um contato na lista ao lado para visualizar o atendimento.
          </S.EmptyState>
        ) : (
          <>
            <S.ChatHeader>
              <div className="cliente">
                <S.Avatar>{initials(selected.name || selected.external_id)}</S.Avatar>
                <div className="info">
                  <strong>{selected.name || selected.external_id}</strong>
                  <span>
                    {titulo} · ID {selected.external_id}
                    {selected.budget ? ` · Interesse: ${selected.budget}` : ""}
                  </span>
                </div>
              </div>
              <div className="acoes">
                {selected.status === "blocked" || selected.status === "paused" ? (
                  <S.ActionButton $variant="gold" onClick={() => handleStatus("active")}>
                    Desbloquear
                  </S.ActionButton>
                ) : (
                  <S.ActionButton $variant="danger" onClick={() => handleStatus("blocked")}>
                    Bloquear
                  </S.ActionButton>
                )}
              </div>
            </S.ChatHeader>

            <S.MessagesScroll ref={scrollRef} onScroll={handleScroll}>
              {messages.map((m) => {
                const fromIA = m.role === "assistant";
                return (
                  <S.MsgRow key={m.id} $fromIA={fromIA}>
                    <div className="quem">
                      {fromIA ? "Atendimento" : "Cliente"}
                      <b>{hora(m.created_at)}</b>
                    </div>
                    <S.Bubble $fromIA={fromIA}>{renderFormatted(m.content)}</S.Bubble>
                  </S.MsgRow>
                );
              })}
            </S.MessagesScroll>

          </>
        )}
      </S.ChatWindow>
    </S.ChatLayout>
  );
}
